import { signal } from "@preact/signals";

import { clearSession, token } from "~/lib/session.js";

import Query, { clearCache } from "~/util/query.js";
import { default as request, NetworkError, RequestError } from "~/util/request.js";
import { captureError } from "~/util/sentry.js";
import { array, is, number, object, optional, parse, string } from "~/util/validate.js";
/** @import { StandardSchemaV1 } from "~/util/validate.js"; */
/** @import { RequestOptions } from "~/util/request.js"; */
/** @import { ApiQueryFn } from "~/backend/index.js"; */

export const apiHost = signal("https://api.phaze.app");

apiHost.subscribe(() => clearCache());

/** @param {string} path */
function resolve(path) {
	if (!apiHost.value) throw new Error("No API host configured.");
	return `${apiHost.value}${path}`;
}

/**
 * @template [T=unknown]
 * @typedef {RequestOptions & { schema?: StandardSchemaV1<unknown, T> }} ApiRequestOptions
 */

/**
 * Make an authenticated request to the Phaze API, parsing the response with `options.schema` if
 * given.
 *
 * @template [T=unknown]
 * @param {"GET" | "POST" | "PUT" | "PATCH" | "DELETE"} method
 * @param {string} url
 * @param {ApiRequestOptions<T>} [options]
 * @returns {Promise<T>}
 */
export async function authedApiRequest(method, url, options) {
	if (!token.value) {
		throw new ApiError(401, [{ code: "unauthorized", message: "No authentication token." }]);
	}

	try {
		return await apiRequest(method, url, { ...options, authorization: token.value });
	} catch (e) {
		const err = e instanceof Error ? ApiError.from(e) : undefined;
		// if the auth token is not accepted, clear it
		if (err?.status === 401) clearSession();

		// re-throw the error
		throw e;
	}
}

/**
 * Make a request to the Phaze API, parsing the response with `options.schema` if given.
 *
 * @template [T=unknown]
 * @param {"GET" | "POST" | "PUT" | "PATCH" | "DELETE"} method
 * @param {string} path
 * @param {ApiRequestOptions<T>} [options]
 * @returns {Promise<T>}
 */
export async function apiRequest(method, path, options = {}) {
	const { schema, ...opts } = options;

	try {
		const res = await request(method, resolve(path), opts);

		if (!schema) return /** @type {T} */ (res);
		return parse(schema, res);
	} catch (e) {
		captureError(e, "Error", { method, path });
		throw e;
	}
}

/**
 * A validator for matching the Phaze API response schema
 *
 * @template T
 * @param {StandardSchemaV1<unknown, T>} data
 */
export const response = data => object({ status: string, data });

/**
 * A validator for matching the Phaze API paginated response schema
 *
 * @template T
 * @param {StandardSchemaV1<unknown, T>} data
 */
export const paginatedResponse = data =>
	object({ status: string, data: array(data), count: number });

/**
 * @typedef {object} ResponseError
 * @property {string} code
 * @property {string} message
 * @property {string | undefined} [field]
 * @property {string | undefined} [tag]
 */

/** A validator for matching the Phaze API error schema */
const error = object({
	status: string,
	errors: array(
		object({
			code: string,
			message: string,
			field: optional(string),
			tag: optional(string),
		}),
	),
});

/** @template {string} [T =string] */
export class ApiError extends Error {
	/** A symbol indicating messages without a field. */
	static noField = Symbol("no field");

	/** @type {ResponseError[]} */
	errors = [];

	/**
	 * @param {ResponseError} error
	 * @param {T | typeof ApiError.noField} field
	 */
	#message(error, field) {
		const key = error.tag ? `${error.code}.${error.tag}` : error.code;
		return this.messages[field]?.[key] ?? error.message;
	}

	status = -1;

	/**
	 * A map between field names, error codes and human-readable error messages, meant to be
	 * overridden in subclasses.
	 *
	 * Each key is a field name, with `ApiError.noField` as a special symbol for errors without codes,
	 * and each value is a map of error codes to human-readable error messages. To specify a tag,
	 * append it to the code name delimited by a dot.
	 *
	 * For example, the below class contains messages for the errors `{ code: "invalid_credentials" }`
	 * and `{ field: "password", code: "validation_failed", tag: "required" }`:
	 *
	 * ```js
	 * /** extends {ApiError<"password">}
	 * class LogInError extends ApiError {
	 * 	get messages() {
	 * 		return {
	 * 			[ApiError.noField]: {
	 * 				invalid_credentials: "Invalid email or password.",
	 * 			},
	 * 			password: {
	 * 				"validation_failed.required": "Password is required.",
	 * 			},
	 * 		};
	 * 	}
	 * }
	 * ```
	 */
	get messages() {
		return /** @type {{ [K in typeof ApiError.noField | T]: { [code in string]: string } }} */ ({});
	}

	/** An object of field errors present in the response, if any. */
	fields = new Proxy(
		/** @type {Record<T, ResponseError | undefined>} */ ({}),
		{
			get: (_target, /** @type {T} */ field) => {
				const name = field.toLowerCase();
				const e = this.errors.find(e => e.field?.toLowerCase() === name);
				if (!e) return;

				return { ...e, message: this.#message(e, field) };
			},
		},
	);

	/**
	 * @param {number} status
	 * @param {ResponseError[]} errors
	 */
	constructor(status, errors) {
		super("");
		this.status = status;
		this.errors = errors;

		// set `this.message` from the errors
		const e = this.errors.find(e => e.field === undefined);
		if (e) this.message = this.#message(e, ApiError.noField);
	}

	get code() {
		return this.errors.find(e => e.field === undefined)?.code ?? "unknown_error";
	}

	/**
	 * Create an `ApiError` from a JavaScript error
	 *
	 * @template {string} Fields
	 * @template {typeof ApiError<Fields>} Subclass
	 * @this {Subclass}
	 * @param {unknown} e
	 * @returns {InstanceType<Subclass>}
	 */
	static from(e) {
		const Err = this;

		// if `e` is an instance of ApiError, convert it to this subclass of ApiError
		if (e instanceof ApiError) {
			return /** @type {InstanceType<Subclass>} */ (new Err(e.status, e.errors));
		}

		// if `e` is an instance of RequestError, convert it to this subclass of ApiError
		if (e instanceof RequestError && is(error, e.json)) {
			return /** @type {InstanceType<Subclass>} */ (new Err(e.status, e.json.errors));
		}

		// otherwise, create an ApiError as best we can
		const message = e instanceof Error ? e.message : `${e}`;
		const errors = [{ code: "unknown_error", message, field: undefined, tag: undefined }];
		return /** @type {InstanceType<Subclass>} */ (new Err(-1, errors));
	}
}

/**
 * @template {any[]} const Key
 * @template {any} Data
 * @extends {Query<Key, Data>}
 */
class ApiQuery extends Query {
	/** @override */
	get error() {
		if (!super.error) return undefined;
		return ApiError.from(super.error);
	}
}

/** @type {ApiQueryFn} */
export const query = (get, options) => {
	return new ApiQuery(get, async ([k, query]) => {
		const req = options.noAuth ? apiRequest : authedApiRequest;
		// the schema may validate only a subset of the payload, so the response is typed by the spec
		const res = await req("GET", k, { query: /** @type {any} */ (query), schema: options.schema });
		return /** @type {any} */ (res);
	});
};

/**
 * Whether an error represents a normal, expected outcome of an API request (an error response
 * from the backend, or a network failure) rather than a bug in our own code.
 *
 * @param {unknown} e
 */
export function isExpectedError(e) {
	return e instanceof ApiError || e instanceof NetworkError || e instanceof RequestError;
}
